import { useEffect, useRef, useState } from "react";
import { Phone, MessageCircle, MapPin, ChevronUp, X } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * FooterContactWheel
 *
 * A premium FAB-style "contact button" rendered inside the footer. When the
 * user clicks it, three icons (Phone / WhatsApp / Map) bloom outward in a
 * fan. Each opens its respective native handler:
 *   • Phone → tel: link (call)
 *   • WhatsApp → wa.me link (chat)
 *   • Map → external Google Maps location
 *
 * Click anywhere outside the wheel collapses it.
 */
export default function FooterContactWheel({ phone, whatsapp, locationUrl }) {
  const { lang, dir, theme } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const labels = lang === "ar"
    ? { call: "اتصال", wa: "محادثة", map: "الموقع", open: "تواصل معنا" }
    : lang === "ur"
      ? { call: "کال", wa: "گفتگو", map: "مقام", open: "ہم سے رابطہ" }
      : { call: "Call", wa: "Chat", map: "Location", open: "Contact us" };

  const dial = (s) => (s || "").replace(/[\s+()-]/g, "");
  const telHref = phone ? `tel:${(phone || "").replace(/\s/g, "")}` : "";
  const waHref = whatsapp ? `https://wa.me/${dial(whatsapp)}` : "";
  const mapHref = locationUrl || "";

  // Bloom angles & radius — the 3 chips fan out above the main button.
  const RADIUS = 92;
  const chipDefs = [
    { key: "call",  href: telHref, target: "_self",   Icon: Phone,         label: labels.call, color: "#1e8e3e", show: !!phone, angle: -120 },
    { key: "wa",    href: waHref,  target: "_blank",  Icon: MessageCircle, label: labels.wa,   color: "#25D366", show: !!whatsapp, angle: -90 },
    { key: "map",   href: mapHref, target: "_blank",  Icon: MapPin,        label: labels.map,  color: "#d4af37", show: !!mapHref, angle: -60 },
  ];

  const css = {
    wrap: {
      position: "relative",
      width: "64px",
      height: "64px",
      // a bit of extra reserved space so the bloom doesn't get clipped
      // by the footer's overflow.
      pointerEvents: "auto",
    },
    main: {
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      background: open ? theme.gold : "transparent",
      color: open ? "#1a1410" : theme.gold,
      border: `2px solid ${theme.gold}`,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background 200ms ease, color 200ms ease, transform 240ms cubic-bezier(0.4,0,0.2,1)",
      transform: open ? "rotate(45deg)" : "rotate(0deg)",
      boxShadow: open
        ? `0 0 0 8px rgba(212,175,55,0.10), 0 12px 28px rgba(212,175,55,0.30)`
        : `0 8px 22px rgba(0,0,0,0.18)`,
    },
    pulseRing: {
      position: "absolute",
      inset: "-6px",
      borderRadius: "50%",
      border: `1.5px solid ${theme.gold}`,
      opacity: open ? 0 : 0.6,
      animation: open ? "none" : "vta-wheel-pulse 2.6s ease-out infinite",
      pointerEvents: "none",
    },
    chip: (angle, idx) => {
      const rad = (angle * Math.PI) / 180;
      const x = Math.cos(rad) * RADIUS;
      const y = Math.sin(rad) * RADIUS;
      return {
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "52px",
        height: "52px",
        marginTop: "-26px",
        marginLeft: "-26px",
        borderRadius: "50%",
        background: theme.surface,
        border: `1.5px solid ${theme.borderAccent}`,
        color: theme.text,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textDecoration: "none",
        boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
        transform: open
          ? `translate(${x}px, ${y}px) scale(1)`
          : "translate(0,0) scale(0.6)",
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
        transition: `transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1) ${idx * 60}ms, opacity 240ms ease ${idx * 60}ms`,
      };
    },
    chipIconBox: (color) => ({
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color,
    }),
    chipLabel: (angle) => {
      // Render a small text label just outside each chip (further from
      // centre on the same angle).
      const rad = (angle * Math.PI) / 180;
      const x = Math.cos(rad) * (RADIUS + 38);
      const y = Math.sin(rad) * (RADIUS + 38);
      return {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: open
          ? `translate(${x - 36}px, ${y - 10}px)`
          : "translate(0,0) scale(0.6)",
        opacity: open ? 1 : 0,
        width: "72px",
        textAlign: "center",
        fontSize: "10.5px",
        fontWeight: 700,
        color: theme.text,
        background: theme.surface,
        border: `1px solid ${theme.borderSoft}`,
        borderRadius: "999px",
        padding: "3px 6px",
        pointerEvents: "none",
        transition: "transform 380ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 240ms ease",
        transitionDelay: open ? "120ms" : "0ms",
        whiteSpace: "nowrap",
      };
    },
  };

  return (
    <div style={css.wrap} ref={ref} dir={dir} data-testid="footer-contact-wheel">
      <div style={css.pulseRing} aria-hidden />
      <button
        type="button"
        aria-label={labels.open}
        title={labels.open}
        onClick={() => setOpen((v) => !v)}
        style={css.main}
        data-testid="footer-contact-toggle"
      >
        {open ? <X size={22} strokeWidth={2.4} /> : <ChevronUp size={22} strokeWidth={2.4} />}
      </button>

      {chipDefs.filter((c) => c.show).map((c, i) => (
        <a
          key={c.key}
          href={c.href}
          target={c.target}
          rel={c.target === "_blank" ? "noopener noreferrer" : undefined}
          style={css.chip(c.angle, i)}
          data-testid={`footer-contact-${c.key}`}
          aria-label={c.label}
          title={c.label}
        >
          <span style={css.chipIconBox(c.color)}>
            <c.Icon size={22} strokeWidth={2.2} />
          </span>
        </a>
      ))}

      {chipDefs.filter((c) => c.show).map((c) => (
        <span key={`l-${c.key}`} style={css.chipLabel(c.angle)} aria-hidden>
          {c.label}
        </span>
      ))}

      <style>{`
        @keyframes vta-wheel-pulse {
          0%   { transform: scale(1);   opacity: 0.55; }
          70%  { transform: scale(1.4); opacity: 0; }
          100% { transform: scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
